<script>
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { RefundPath } from "@/path.js";

/**
 * Refund modal for a paid, delivered HoReCa order.
 *
 * Loads the backend preview (what was paid for vs. what arrived, per gravy,
 * with the refund amount the backend will raise), lets ops raise it with a
 * confirm, shows the existing refund's Razorpay status, and offers a retry
 * when Razorpay rejected it. All money math is server-side.
 */
export default {
  props: ["selectedOrder"],
  data() {
    return {
      baseUrl: process.env.VUE_APP_API_BACKEND_URL,
      loading: false,
      submitting: false,
      error: null,
      preview: null,
      fields: [
        { key: "name", label: "Item" },
        { key: "ordered_qty", label: "Paid for", class: "text-right" },
        { key: "delivered_qty", label: "Delivered", class: "text-right" },
        { key: "missed_qty", label: "Short", class: "text-right" },
        { key: "unit_price", label: "Unit price", class: "text-right" },
        { key: "amount", label: "Refund ₹", class: "text-right" },
      ],
    };
  },
  computed: {
    refund() {
      return this.preview ? this.preview.existing_refund : null;
    },
    status() {
      return this.refund ? this.refund.status : null;
    },
    statusVariant() {
      return this.status == "processed"
        ? "success"
        : this.status == "failed"
        ? "danger"
        : "warning";
    },
    statusLabel() {
      return this.status == "processed"
        ? "Refunded"
        : this.status == "failed"
        ? "Failed"
        : "Processing";
    },
    // Rows shown in the table: the raised breakdown when a refund exists, else the preview.
    rows() {
      if (this.refund) {
        return (this.refund.items || []).map((i) => ({
          ...i,
          amount: i.amount_in_rupees,
        }));
      }
      return (this.preview?.items || []).map((i) => ({
        ...i,
        amount: i.amount,
      }));
    },
    total() {
      return this.refund
        ? this.refund.refund_amount_in_rupees
        : this.preview?.total || 0;
    },
    canRaise() {
      return this.preview && this.preview.eligible && !this.refund && this.total > 0;
    },
    canRetry() {
      return this.refund && this.status == "failed";
    },
    excludedText() {
      return (this.preview?.excluded_items || [])
        .map((x) => `${x.name} (${x.reason})`)
        .join(", ");
    },
  },
  methods: {
    fmt(v) {
      return v ? moment(v).format("DD MMM YYYY, HH:mm") : "—";
    },
    rupees(paise) {
      return (Number(paise || 0) / 100).toFixed(2);
    },
    async load() {
      if (!this.selectedOrder || !this.selectedOrder.id) return;
      this.loading = true;
      this.error = null;
      try {
        const res = await axios.get(
          this.baseUrl + RefundPath.PREVIEW(this.selectedOrder.id)
        );
        if (res.data.status) {
          this.preview = res.data.data;
        } else {
          this.error = res.data.message || "Could not load refund preview";
        }
      } catch (e) {
        this.error =
          e.response?.data?.message || "Could not load refund preview";
      } finally {
        this.loading = false;
      }
    },
    confirm(title, text, confirmText) {
      return Swal.mixin({
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      }).fire({
        title,
        text,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: confirmText,
        cancelButtonText: "No, cancel",
        reverseButtons: true,
      });
    },
    async submit(url, successMsg) {
      if (this.submitting) return;
      this.submitting = true;
      try {
        const res = await axios.post(url, {});
        if (res.data.status) {
          this.$bvToast.toast(successMsg, {
            title: "Refund",
            variant: "success",
            solid: true,
          });
          this.$emit("update");
          await this.load();
        } else {
          Swal.fire("Refund not raised", res.data.message || "", "error");
          await this.load();
        }
      } catch (e) {
        Swal.fire(
          "Refund not raised",
          e.response?.data?.message || e.message || "Unknown error",
          "error"
        );
        await this.load();
      } finally {
        this.submitting = false;
      }
    },
    async raise() {
      const r = await this.confirm(
        "Raise refund?",
        `This sends ₹${this.total} back to the partner's original payment method via Razorpay. This cannot be undone.`,
        `Yes, refund ₹${this.total}`
      );
      if (!r.isConfirmed) return;
      await this.submit(
        this.baseUrl + RefundPath.RAISE(this.selectedOrder.id),
        "Refund raised at Razorpay"
      );
    },
    async retry() {
      const r = await this.confirm(
        "Retry refund?",
        "The previous attempt was rejected by Razorpay. The amount will be recomputed from the current delivered quantities and raised again.",
        "Yes, retry"
      );
      if (!r.isConfirmed) return;
      await this.submit(
        this.baseUrl + RefundPath.RETRY(this.selectedOrder.id),
        "Refund re-raised at Razorpay"
      );
    },
  },
  watch: {
    selectedOrder: {
      immediate: true,
      handler() {
        this.preview = null;
        this.load();
      },
    },
  },
};
</script>

<template>
  <b-card>
    <div v-if="loading" class="text-center p-4">
      <b-spinner small></b-spinner> Loading refund details…
    </div>

    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>

    <div v-else-if="preview">
      <!-- Payment summary -->
      <div class="row mb-3">
        <div class="col-md-3">
          <small class="text-muted">Order</small>
          <div class="font-weight-bold">#{{ preview.order_number }}</div>
          <b-badge variant="light" class="text-capitalize">{{ preview.order_status }}</b-badge>
        </div>
        <div class="col-md-3">
          <small class="text-muted">Paid via Razorpay</small>
          <div class="font-weight-bold">
            ₹{{ preview.payment ? preview.payment.amount : "—" }}
          </div>
          <small class="text-muted">{{ preview.payment ? preview.payment.payment_order_id : "" }}</small>
        </div>
        <div class="col-md-3">
          <small class="text-muted">Delivery charge</small>
          <div class="font-weight-bold">₹{{ rupees(preview.delivery_charge_paise) }}</div>
          <small class="text-muted">not refundable</small>
        </div>
        <div class="col-md-3">
          <small class="text-muted">Max refundable</small>
          <div class="font-weight-bold">₹{{ rupees(preview.max_refundable_paise) }}</div>
          <small v-if="preview.gateway_checked === false" class="text-warning">Razorpay not reachable; based on our records</small>
        </div>
      </div>

      <!-- Existing refund -->
      <b-alert v-if="refund" show :variant="statusVariant" class="mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <strong>Refund ₹{{ refund.refund_amount_in_rupees }}</strong>
            <b-badge :variant="statusVariant" class="ml-2">{{ statusLabel }}</b-badge>
          </div>
          <b-button
            v-if="canRetry"
            size="sm"
            variant="danger"
            :disabled="submitting"
            @click="retry"
          >
            <b-spinner v-if="submitting" small></b-spinner>
            Retry refund
          </b-button>
        </div>
        <div class="row mt-2 small">
          <div class="col-md-4">Razorpay refund: <code>{{ refund.razorpay_refund_id || "—" }}</code></div>
          <div class="col-md-4">Payment: <code>{{ refund.razorpay_payment_id || "—" }}</code></div>
          <div class="col-md-4">Receipt: <code>{{ refund.receipt || "—" }}</code></div>
          <div class="col-md-4">Raised: {{ fmt(refund.created_at) }}</div>
          <div class="col-md-4">Processed: {{ fmt(refund.processed_at) }}</div>
          <div class="col-md-4">Bank ARN: {{ refund.utr_number || "—" }}</div>
          <div class="col-md-4">Attempts: {{ refund.attempts }}</div>
          <div class="col-md-4">
            Raised: {{ refund.raised_by ? "by admin #" + refund.raised_by : "automatically on delivery" }}
          </div>
          <div class="col-md-8" v-if="refund.failure_reason">
            <span class="text-danger">Reason: {{ refund.failure_reason }}</span>
          </div>
        </div>
      </b-alert>

      <!-- Not eligible -->
      <b-alert v-else-if="!preview.eligible" show variant="secondary">
        {{ preview.reason || "This order is not eligible for a refund." }}
      </b-alert>

      <!-- Nothing short -->
      <b-alert v-else-if="rows.length === 0" show variant="success">
        Every paid quantity was delivered. Nothing to refund.
      </b-alert>

      <!-- Breakdown -->
      <b-table
        v-if="rows.length > 0"
        :items="rows"
        :fields="fields"
        small
        striped
        hover
        foot-clone
        class="mb-2"
      >
        <template v-slot:cell(unit_price)="row">₹{{ Number(row.item.unit_price).toFixed(2) }}</template>
        <template v-slot:cell(amount)="row">
          <strong>₹{{ Number(row.item.amount).toFixed(2) }}</strong>
        </template>
        <template v-slot:foot(name)>Total</template>
        <template v-slot:foot(ordered_qty)><span></span></template>
        <template v-slot:foot(delivered_qty)><span></span></template>
        <template v-slot:foot(missed_qty)><span></span></template>
        <template v-slot:foot(unit_price)><span></span></template>
        <template v-slot:foot(amount)>
          <strong>₹{{ Number(total).toFixed(2) }}</strong>
        </template>
      </b-table>

      <b-alert v-if="!refund && preview.capped" show variant="warning" class="py-2">
        Capped at the amount Razorpay still holds for this payment.
      </b-alert>

      <div v-if="!refund && preview.excluded_items && preview.excluded_items.length" class="text-muted small mb-3">
        Not refundable: {{ excludedText }}
      </div>

      <div class="text-right" v-if="canRaise">
        <b-button variant="success" :disabled="submitting" @click="raise">
          <b-spinner v-if="submitting" small></b-spinner>
          Raise refund ₹{{ Number(total).toFixed(2) }}
        </b-button>
      </div>
    </div>
  </b-card>
</template>
